import {GetValues} from "js-vextensions";
import {GraphRenderType, SmoothingType} from "../../../Store/main/tools/journey.js";
import {StatsGrouping, StatsXType, StatsYType} from "./@StatTypes.js";
import moment from "moment";
import {O} from "web-vcore";
import {AddSchema, GetSchemaJSON, MGLClass} from "mobx-graphlink";

export enum RangeType {
	start = "start",
	count = "count",
}
AddSchema("RangeType", {enum: GetValues(RangeType)});

export enum ShowTechExecution {
	hidden = "hidden",
	percent = "percent",
	binary = "binary",
}
AddSchema("ShowTechExecution", {enum: GetValues(ShowTechExecution)});

export const StatView_metaKeys = ["id", "creator", "createdAt", "name"] as const;
export const StatView_NormalKeys = ()=>Object.keys(GetSchemaJSON("StatView").properties!).Exclude(...StatView_metaKeys);

@MGLClass({table: "statViews"})
export class StatView {
	static WillApplySmoothing(view: StatView) { return (view.smoothing ?? 0) > 0 && view.renderType != "bars"; }

	constructor(data?: Partial<StatView>) {
		Object.assign(this, data);
	}

	id: string;
	creator?: string;
	createdAt?: number;
	name?: string;

	xType: StatsXType = "dayOffset";
	yType: StatsYType = "termsInLongText";
	combined_yTypes: StatsYType[] = ["quizPrompts_sum", "quizFirstTryHitPercent"];
	combined_yTypes_scales: {[key: string]: number} = {quizPrompts_sum: 500, quizFirstTryHitPercent: 100};
	grouping = StatsGrouping.none;

	// dropdown
	// ==========

	renderType: GraphRenderType = "bars";
	middleKeepPercent = 1; // 0-1
	replaceYValNullWith: number|n; // currently, UI is only setup to make this null/undefined, or 0
	smoothing = 0;
	smoothingType = SmoothingType.previous;
	dayRange_type = RangeType.count;
	dayRange_start = moment("2020-01-01").valueOf();
	dayRange_count = 30;
	days_omitLastX = 0;
	cyclesToShow = 30;

	dreamMatcher_text = "";
	showLucidCount = true;
	showLucidRate = true;
	showNightCount = true;
	showTechExecution: ShowTechExecution = ShowTechExecution.hidden;
	techExecution_minForPass = 1; // only used if showTechExecution is "binary"
	//showMPTargetPromptCount? = false;
	maxMemoryPromptRecallGap = 60; // in minutes
	normalizeGroupMetrics = true;
	normalizeGroupMetrics_alarmDelay = 80; // in minutes
	lucidityLevels: LucidityLevel[]|n;
	dateRange_enabled = false;
	dateRange_min: number|n = moment("2020-01-01").valueOf();
	dateRange_max: number|n = moment("2020-01-01").valueOf();
	showDateMarkers = true;
	dateMarkers = [] as Array<number|DateMarker|n>;
	groupRenames = [] as {from: string, to: string}[];
	yAxis_labelOverride = "";
	xAxis_labelOverride = "";
	xAxis_stepOverride_enabled = false;
	xAxis_stepOverride = 0;
	xAxis_maxOverride_enabled = false;
	xAxis_maxOverride = 0;
	yAxis_maxOverride_enabled = false;
	yAxis_maxOverride = 0;
	legend_heightOverride: number|n;
}
//export type StatView = Partial<StatViewFull>;

export const LucidityLevel_values = ["nonLucid", "semiLucid", "lucid"] as const;
export type LucidityLevel = typeof LucidityLevel_values[number];

AddSchema("StatView", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},
		name: {type: "string"},
		xType: {$ref: "StatsXType"},
		yType: {$ref: "StatsYType"},
		combined_yTypes: {items: {$ref: "StatsYType"}},
		combined_yTypes_scales: {patternProperties: {"\\w+": {type: "number"}}},
		grouping: {$ref: "StatsGrouping"},

		renderType: {$ref: "GraphRenderType"},
		middleKeepPercent: {type: "number"},
		smoothing: {type: "number"},
		smoothingType: {$ref: "SmoothingType"},
		dayRange_type: {$ref: "RangeType"},
		dayRange_start: {type: "number"},
		dayRange_count: {type: "number"},
		days_omitLastX: {type: "number"},
		cyclesToShow: {type: "number"},

		dreamMatcher_text: {type: "string"},
		showLucidCount: {type: "boolean"},
		showLucidRate: {type: "boolean"},
		showNightCount: {type: "boolean"},
		showTechExecution: {$ref: "ShowTechExecution"},
		techExecution_minForPass: {type: "number"},
		normalizeGroupMetrics: {type: "boolean"},
		normalizeGroupMetrics_alarmDelay: {type: "number"},
		lucidityLevels: {items: {enum: LucidityLevel_values as any}},
		dateRange_enabled: {type: "boolean"},
		dateRange_min: {type: ["number", "null"]},
		dateRange_max: {type: ["number", "null"]},
		showDateMarkers: {type: "boolean"},
		dateMarkers: {items: {oneOf: [{type: ["number", "null"]}, {$ref: "DateMarker"}]}},
		groupRenames: {items: {properties: {from: {type: "string"}, to: {type: "string"}}}},
		yAxis_labelOverride: {type: "string"},
		xAxis_labelOverride: {type: "string"},
		xAxis_stepOverride_enabled: {type: "boolean"},
		xAxis_stepOverride: {type: "number"},
		xAxis_maxOverride_enabled: {type: "boolean"},
		xAxis_maxOverride: {type: "number"},
		yAxis_maxOverride_enabled: {type: "boolean"},
		yAxis_maxOverride: {type: "number"},
		legend_heightOverride: {type: "number"},
	},
});

export class DateMarker {
	constructor(data?: Partial<DateMarker>) {
		Object.assign(this, data);
	}
	date: number|n;
	color?: string|n;
}
AddSchema("DateMarker", {
	properties: {
		date: {type: ["number", "null"]},
		color: {type: ["string", "null"]},
	},
});

export function MarkerFromMarkerOrDate(markerOrDate: number|DateMarker|n) {
	if (markerOrDate == null) return null;
	if (typeof markerOrDate == "number") return new DateMarker({date: markerOrDate});
	return markerOrDate as DateMarker;
}